import { bind } from "@react-rxjs/core"
import { createSignal } from "@react-rxjs/utils"
import { t } from "i18next"
import {
  catchError,
  debounceTime,
  defer,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  reduce,
  retry,
  shareReplay,
  switchMap,
  tap,
} from "rxjs"

import {
  type EquityError,
  EquityErrorType,
  ERROR_EQUITY_INSTRUMENT_RESULT,
} from "@/generated/TradingGateway"
import { withConnection } from "@/services/withConnection"

import { clearEquitiesError, publishEquitiesError } from "../errorAlert"
import { equityErrorKey } from "../errorAlert/equityErrorTranslationKey"
import {
  type EquityInstrumentResult,
  EquityInstrumentService,
  type Instrument,
  type InstrumentRequest,
} from "../TradingGateway"

const publishInstrumentsError = (errorKey: string, searchStr: string) => {
  const message = t(errorKey)
  publishEquitiesError({
    source: "instruments",
    message: searchStr
      ? t("instruments.errors.forQuery", { message, query: searchStr })
      : message,
  })
}

type InstrumentMap = Map<string, Instrument>

/**
 * The gateway streams one result per instrument, so a server-returned error
 * frame can arrive interleaved with valid instruments. Carrying both out of
 * `reduce` lets a single terminal `tap` decide publish-vs-clear from the whole
 * response rather than from whichever frame happened to arrive last.
 */
type InstrumentsResponse = {
  instruments: InstrumentMap
  error: EquityError | null
}

const [_searchValue$, setSearchValue] = createSignal<string>()
const [useSearchValue, searchValue$] = bind(_searchValue$, "")

export { setSearchValue, useSearchValue }

const searchByStringCache = new Map<string, Observable<InstrumentMap>>()

const getInstruments$ = (
  searchStr: string,
  symbols: string[],
): Observable<InstrumentMap> => {
  const searchKey = searchStr.toUpperCase()
  const cachedSearchResponse = searchByStringCache.get(searchKey)

  if (cachedSearchResponse) {
    return cachedSearchResponse
  }

  // `shareReplay(1)` would otherwise replay a failed lookup for the lifetime of
  // the app; dropping the entry lets the next identical search reach the gateway.
  const evictFromCache = () => searchByStringCache.delete(searchKey)

  const instruments$ = defer(() =>
    EquityInstrumentService.getInstruments({
      searchStr,
      symbols,
      limit: 300, // safe limit to prevent excessively large responses, browser crashes with 500+ instruments
    } satisfies InstrumentRequest),
  ).pipe(
    reduce<EquityInstrumentResult, InstrumentsResponse>(
      (acc, curr) => {
        if (curr.type === ERROR_EQUITY_INSTRUMENT_RESULT) {
          return { ...acc, error: curr.payload }
        }

        const { payload } = curr.payload
        return {
          ...acc,
          instruments: new Map(acc.instruments).set(payload.symbol, payload),
        }
      },
      { instruments: new Map<string, Instrument>(), error: null },
    ),
    retry({ count: 3, delay: 3_000 }),
    tap(({ error }) => {
      if (error) {
        // the raw failure stays in the console; users get generic translated copy
        console.error("[instruments] Server-returned error:", error)
        publishInstrumentsError(
          equityErrorKey("instruments", error.errorType),
          searchStr,
        )
        evictFromCache()
        return
      }
      clearEquitiesError("instruments")
    }),
    map(({ instruments }) => instruments),
    catchError((err) => {
      console.error("[instruments] Hydra stream error:", err)
      publishInstrumentsError(
        equityErrorKey("instruments", EquityErrorType.InternalError),
        searchStr,
      )
      evictFromCache()
      return of(new Map<string, Instrument>())
    }),
    shareReplay(1),
  )

  if (searchStr) {
    searchByStringCache.set(searchKey, instruments$)
  }
  return instruments$
}

// searchbox instrument list
const searchedInstruments$ = searchValue$.pipe(
  filter((text) => text !== null && text.length > 0),
  distinctUntilChanged(),
  debounceTime(500),
  switchMap((searchStr) => getInstruments$(searchStr, [])),
  map((instruments) => Array.from(instruments.values())),
)

// instrument info for individual components
const getSymbols$ = (symbols: string[]) =>
  getInstruments$("", symbols).pipe(withConnection())

export const [useSearchResults, searchResults$] = bind<Instrument[]>(
  searchedInstruments$,
  [],
)

export const [useInstrumentMap, instrumentMap$] = bind<
  [key: string],
  InstrumentMap
>((key: string) => getSymbols$([...new Set(key.split(","))]), new Map())

export const [useInstrument, instrument$] = bind<
  [key: string],
  Instrument | undefined
>(
  (key: string) => getSymbols$([key]).pipe(map((map) => map.get(key))),
  undefined,
)
